import clsx from 'clsx';
import React, { forwardRef, createContext, useEffect, useState, CSSProperties, ReactNode } from 'react';

interface SidebarContextValue {
	collapsed: boolean;
	rtl: boolean;
	toggled: boolean;
}

export const SidebarContext = createContext<SidebarContextValue>({
	collapsed: false,
	rtl: false,
	toggled: false,
});

type BreakPoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

interface MenuWrapperProps {
	children?: ReactNode;
	className?: string;
	width?: string | number;
	collapsedWidth?: string | number;
	collapsed?: boolean;
	rtl?: boolean;
	toggled?: boolean;
	image?: string;
	breakPoint?: BreakPoint;
	onToggle?: (toggled: boolean) => void;
	style?: CSSProperties;
}

const MenuWrapper = forwardRef<HTMLDivElement, MenuWrapperProps>(
	(
		{
			children,
			className,
			width,
			collapsedWidth,
			collapsed = false,
			rtl = false,
			toggled = false,
			image,
			breakPoint,
			onToggle,
			style = {},
			...rest
		},
		ref,
	) => {
		const [sidebarState, setSidebarState] = useState<SidebarContextValue>({
			collapsed: typeof collapsed === 'undefined' ? false : collapsed,
			rtl: typeof rtl === 'undefined' ? false : rtl,
			toggled: typeof toggled === 'undefined' ? false : toggled,
		});

		const sidebarRef = ref || React.createRef<HTMLDivElement>();

		const widthStyle: CSSProperties = width ? { width, minWidth: width } : {};

		const collapsedWidthStyle: CSSProperties = collapsedWidth
			? { width: collapsedWidth, minWidth: collapsedWidth }
			: {};

		const finalWidth = collapsed ? collapsedWidthStyle : widthStyle;

		const handleToggleSidebar = () => {
			const toggleValue = sidebarState.toggled;
			setSidebarState({ ...sidebarState, toggled: !toggleValue });
			if (onToggle) {
				onToggle(!toggleValue);
			}
		};

		useEffect(() => {
			setSidebarState({ ...sidebarState, collapsed, rtl, toggled });
		}, [collapsed, rtl, toggled]);

		return (
			<SidebarContext.Provider value={sidebarState}>
				<div
					ref={sidebarRef}
					className={clsx('sideNav', className, breakPoint, {
						collapsed,
						rtl,
						toggled,
					})}
					style={{ ...finalWidth, ...style }}
					{...rest}
				>
					<div className="sideNav-inner">
						{image ? (
							<img alt="sidebar background" className="sidebar-bg" src={image} />
						) : null}
						<div className="sideNav-layout">{children}</div>
					</div>
					<div
						aria-label="overlay"
						className="overlay"
						role="button"
						tabIndex={0}
						onClick={handleToggleSidebar}
						onKeyPress={handleToggleSidebar}
					/>
				</div>
			</SidebarContext.Provider>
		);
	},
);

MenuWrapper.displayName = 'MenuWrapper';

export default MenuWrapper;
